// PCD-8003: Helpers for the revamped "Add Volume Backend Configuration" wizard.
// Step 1 ("Choose your storage") splits the old flat driver dropdown into an
// Array + Connection Protocol pair. Each Array×Protocol combination resolves to
// one of the existing driver keys in GET_DRIVER_CONFIG, so we reuse that schema
// rather than inventing a new one.
import { GET_DRIVER_CONFIG } from '../helpers'

// A single configuration field as defined in GET_DRIVER_CONFIG, plus the
// user-entered value. `type === 'password'` renders a masked input.
export interface DriverField {
  key: string
  value: string
  required?: boolean
  type?: string
  disabled?: boolean
  tooltip?: string
}

// The four connection protocols offered in step 1 (in display order).
export type Protocol = 'NFS' | 'iSCSI' | 'FC' | 'NVMe'

export const PROTOCOL_ORDER: Protocol[] = ['NFS', 'iSCSI', 'FC', 'NVMe']

export interface StorageArray {
  // Stable internal id used as the SelectableCard id and in params.arrayId.
  id: string
  // Human label shown on the card and in the review step.
  label: string
  // Which section the card lives under in step 1.
  group: 'verified' | 'generic'
  // Verified arrays roll up to a storage vendor (STORAGE_VENDORS id). Step 1
  // shows vendors first, then the arrays for the chosen vendor. Generic drivers
  // (NFS/LVM/Custom) have no vendor.
  vendor?: string
  // Logo asset path. When omitted we fall back to a generic storage icon.
  logo?: string
  // Optional one-line description shown under the label (Figma "Desc Slot").
  description?: string
  // Verified arrays: maps a chosen protocol to a GET_DRIVER_CONFIG driver key.
  protocolDrivers?: Partial<Record<Protocol, string>>
  // Generic/advanced arrays (NFS, LVM, Custom): a single driver that does not
  // depend on a connection protocol, so no protocol is offered for them.
  directDriver?: string
  showOnlyLogo?: boolean
}

// Existing logo assets (see StorageTypeLogos.tsx). Arrays without a dedicated
// asset (Huawei, HPE Nimble, Tintri, and the generic NFS/LVM/Custom options)
// render a generic storage icon until real assets are provided.
const LOGO = {
  netapp: '/ui/images/netapp.png',
  purestorage: '/ui/images/purestorage.png',
  hitachi: '/ui/images/hitachi.png',
  infinidat: '/ui/images/lenovo-infinidat.png',
  hpe3par: '/ui/images/3par.png',
  huawei: '/ui/images/huawei.png',
  hpenimble: '/ui/images/hpe.png',
  tintri: '/ui/images/tintri.png',
  dell: '/ui/images/dell.png',
}

// Single source of truth for step 1's array picker. Driver values below are the
// exact keys present in GET_DRIVER_CONFIG.
export const STORAGE_ARRAYS: StorageArray[] = [
  {
    id: 'netapp',
    label: 'NetApp Data ONTAP',
    group: 'verified',
    vendor: 'netapp',
    logo: LOGO.netapp,
    protocolDrivers: {
      NFS: 'NetAppONTAPNFS',
      iSCSI: 'NetAppONTAPISCSI',
      FC: 'NetAppONTAPFC',
      NVMe: 'NetAppONTAPNVMe',
    },
  },
  {
    id: 'pure',
    label: 'Pure Storage',
    group: 'verified',
    vendor: 'pure',
    logo: LOGO.purestorage,
    protocolDrivers: {
      iSCSI: 'PureStorageISCSI',
      FC: 'PureStorageFC',
      NVMe: 'PureStorageNVME',
    },
  },
  {
    id: 'hitachi',
    label: 'Hitachi',
    group: 'verified',
    vendor: 'hitachi',
    logo: LOGO.hitachi,
    protocolDrivers: { iSCSI: 'HitachiISCSI', FC: 'HitachiFC' },
    showOnlyLogo: true,
  },
  {
    id: 'infinidat',
    label: 'Infinidat',
    group: 'verified',
    vendor: 'lenovo',
    logo: LOGO.infinidat,
    protocolDrivers: { iSCSI: 'InfinidatISCSI', FC: 'InfinidatFC' },
    showOnlyLogo: true,
  },
  {
    id: 'huawei',
    label: 'Huawei Dorado',
    group: 'verified',
    vendor: 'huawei',
    logo: LOGO.huawei,
    protocolDrivers: { iSCSI: 'HuaweiISCSI', FC: 'HuaweiFC' },
  },
  {
    // Multi-array vendor (HPE): the vendor card carries the HPE brand, so the
    // array card shows just the product line ('3PAR').
    id: 'hpe3par',
    label: '3PAR',
    group: 'verified',
    vendor: 'hpe',
    logo: LOGO.hpe3par,
    protocolDrivers: { iSCSI: 'HPE3PARISCSI', FC: 'HPE3PARFC' },
  },
  {
    id: 'hpenimble',
    label: 'Nimble',
    group: 'verified',
    vendor: 'hpe',
    logo: LOGO.hpenimble,
    protocolDrivers: { iSCSI: 'HPENimbleISCSI', FC: 'HPENimbleFC' },
  },
  {
    id: 'tintri',
    label: 'Tintri NFS',
    group: 'verified',
    vendor: 'tintri',
    logo: LOGO.tintri,
    protocolDrivers: { NFS: 'Tintri' },
    showOnlyLogo: true,
  },
  // PCD-1063: Dell-EMC families. Each product line has its own cinder driver class,
  // so they're separate arrays under the shared Dell vendor. The vendor card carries
  // the Dell brand, so array cards show just the product line. Documented protocol
  // per family only.
  {
    id: 'dell-powerstore',
    label: 'PowerStore',
    group: 'verified',
    vendor: 'dell',
    logo: LOGO.dell,
    protocolDrivers: { FC: 'DellPowerStoreFC' },
  },
  {
    id: 'dell-powermax',
    label: 'PowerMax',
    group: 'verified',
    vendor: 'dell',
    logo: LOGO.dell,
    protocolDrivers: { FC: 'DellPowerMaxFC' },
  },
  {
    id: 'dell-unity',
    label: 'Unity',
    group: 'verified',
    vendor: 'dell',
    logo: LOGO.dell,
    protocolDrivers: { FC: 'DellUnityFC' },
  },
  {
    id: 'dell-sc',
    label: 'SC (Compellent)',
    group: 'verified',
    vendor: 'dell',
    logo: LOGO.dell,
    protocolDrivers: { iSCSI: 'DellSCISCSI' },
  },
  // Generic/advanced drivers are not protocol-specific — no protocol is shown.
  {
    id: 'nfs',
    label: 'NFS',
    group: 'generic',
    directDriver: 'NFS',
  },
  {
    id: 'lvm',
    label: 'LVM',
    group: 'generic',
    directDriver: 'LVM',
  },
  {
    id: 'custom',
    label: 'Custom Driver',
    group: 'generic',
    // The fully qualified driver name is entered separately (customDriver).
    directDriver: 'Custom',
  },
]

export const getStorageArray = (arrayId: string): StorageArray | undefined =>
  STORAGE_ARRAYS.find((arr) => arr.id === arrayId)

export interface StorageVendor {
  // Stable vendor id, matched against StorageArray.vendor.
  id: string
  // Human label shown on the vendor card.
  label: string
  // Vendor logo asset path.
  logo?: string
  // When the logo is a full wordmark (it already reads as the vendor name), show
  // the logo only and hide the text label — matching the old verified-array cards.
  showOnlyLogo?: boolean
}

// Step 1's vendor picker (display order). Verified arrays are grouped under
// these vendors; a vendor with a single array auto-selects it (no array picker),
// while multi-array vendors (Dell, HPE) show a second "Storage Array" section.
// Reusable, data-driven config — add a vendor here + tag arrays with its id.
export const STORAGE_VENDORS: StorageVendor[] = [
  { id: 'netapp', label: 'NetApp', logo: LOGO.netapp },
  { id: 'hpe', label: 'HPE', logo: LOGO.hpenimble },
  { id: 'pure', label: 'Pure Storage', logo: LOGO.purestorage },
  { id: 'hitachi', label: 'Hitachi Vantara', logo: LOGO.hitachi, showOnlyLogo: true },
  { id: 'huawei', label: 'Huawei', logo: LOGO.huawei },
  { id: 'lenovo', label: 'Lenovo', logo: LOGO.infinidat, showOnlyLogo: true },
  { id: 'tintri', label: 'Tintri', logo: LOGO.tintri, showOnlyLogo: true },
  { id: 'dell', label: 'Dell', logo: LOGO.dell },
]

// FR-009: Dell is not yet certified on the PCD backend, so it is hidden from every
// Dell surface in the UI (the add-backend vendor picker and the supported-storage
// logo strip). Flip this to `true` once Dell is certified to bring it back
// everywhere. The Dell array data and driver definitions are left in place (not
// deleted) so flipping the flag back on restores Dell with no other change.
export const ENABLE_DELL_DRIVERS = false

// FR-009: The vendors offered when adding a new backend. Same as STORAGE_VENDORS,
// minus Dell while it is gated off. Verified arrays are reachable only through
// their vendor card, so dropping the vendor here is enough to make Dell
// unselectable for a new config.
export const getSelectableVendors = (): StorageVendor[] =>
  ENABLE_DELL_DRIVERS ? STORAGE_VENDORS : STORAGE_VENDORS.filter((vendor) => vendor.id !== 'dell')

// Verified arrays belonging to a vendor, in STORAGE_ARRAYS order.
export const getArraysForVendor = (vendorId: string): StorageArray[] =>
  STORAGE_ARRAYS.filter((arr) => arr.vendor === vendorId)

// The vendor id an array rolls up to (edit mode / locked driver derive the
// selected vendor from the resolved arrayId). '' for generic drivers.
export const getVendorForArray = (arrayId: string): string => getStorageArray(arrayId)?.vendor || ''

// Protocols a given array supports, in display order. Generic arrays
// (directDriver) have no protocol, so this returns an empty list for them.
export const getAvailableProtocols = (arrayId: string): Protocol[] => {
  const array = getStorageArray(arrayId)
  if (!array || !array.protocolDrivers) return []
  return PROTOCOL_ORDER.filter((protocol) => !!array.protocolDrivers[protocol])
}

// Resolve the GET_DRIVER_CONFIG driver key for an array (+ protocol). Generic
// arrays resolve directly; verified arrays resolve via the chosen protocol.
export const resolveDriver = (arrayId: string, protocol: Protocol | ''): string => {
  const array = getStorageArray(arrayId)
  if (!array) return ''
  if (array.directDriver) return array.directDriver
  if (!protocol) return ''
  return array.protocolDrivers?.[protocol] || ''
}

// Reverse lookup: given an already-saved driver key (edit mode or a driver
// enforced by a sibling config), find which array + protocol produced it.
export const getArrayAndProtocolForDriver = (
  driver: string,
): { arrayId: string; protocol: Protocol | '' } => {
  for (const array of STORAGE_ARRAYS) {
    if (array.directDriver === driver) {
      return { arrayId: array.id, protocol: '' }
    }
    for (const protocol of PROTOCOL_ORDER) {
      if (array.protocolDrivers?.[protocol] === driver) {
        return { arrayId: array.id, protocol }
      }
    }
  }
  return { arrayId: '', protocol: '' }
}

// Only these arrays require the extra "Custom Driver Name" input in later steps.
export const isCustomArray = (arrayId: string): boolean => arrayId === 'custom'

// The subset of step-1 params the disabled-Next hint looks at.
interface Step1Params {
  name?: string
  arrayId?: string
  protocol?: string
  customDriver?: string
}

// A user-facing hint naming exactly what step 1 still needs, shown on the disabled
// "Next" button. It names the field that actually applies to the current choice:
// a storage driver when nothing is picked, a custom driver name for the Custom
// driver, or a connection protocol for a verified vendor array. Returns '' when
// step 1 is complete (mirrors AddVolumeBackendConfigWizard's isStep1Complete).
export const getStep1MissingMessage = (params: Step1Params): string => {
  const missing: string[] = []
  if (!params.name) missing.push('a configuration name')
  if (!params.arrayId) {
    missing.push('a storage driver')
  } else if (isCustomArray(params.arrayId) && !params.customDriver) {
    missing.push('a custom driver name')
  } else if (getAvailableProtocols(params.arrayId).length > 0 && !params.protocol) {
    missing.push('a connection protocol')
  }
  return missing.length ? `Provide ${missing.join(' and ')} to continue.` : ''
}

// The widget used to edit an optional field's value in step 3.
export type FieldWidgetType = 'string' | 'boolean' | 'number' | 'percentage'

// Optional keys that should render as a number quantity stepper. Numeric-valued
// keys are NOT auto-detected, because values alone are misleading — e.g.
// `netapp_server_port = 443` must stay a text field per the design. Only integer
// "count/size" knobs are steppers; fractional knobs like
// `max_over_subscription_ratio` (e.g. 1.0, 20.0) stay text fields so their float
// values are preserved. Add new integer keys here as needed.
export const NUMBER_FIELD_KEYS = new Set<string>([
  'image_volume_cache_max_count',
  'image_volume_cache_max_size_gb',
  'netapp_performance_cache_expiry_duration',
  'netapp_dedupe_cache_expiry_duration',
])

const isBooleanValue = (value: string): boolean =>
  value?.toLowerCase() === 'true' || value?.toLowerCase() === 'false'

// Classifies an optional field into the widget it should render with. Order
// matters: percentage (by key name) and boolean (by value) are reliable; number
// is an explicit allow-list; everything else is a plain text field.
export const getFieldWidgetType = (field: DriverField): FieldWidgetType => {
  if (field.key.includes('percentage')) return 'percentage'
  if (isBooleanValue(field.value)) return 'boolean'
  if (NUMBER_FIELD_KEYS.has(field.key)) return 'number'
  return 'string'
}

// All configuration fields defined for a driver (required + optional).
export const getDriverFields = (driver: string): DriverField[] =>
  (GET_DRIVER_CONFIG[driver]?.keyValues as DriverField[]) || []

// The mandatory (required) fields shown in step 2.
export const getMandatoryFields = (driver: string): DriverField[] =>
  getDriverFields(driver).filter((field) => field.required)

// The optional fields shown in step 3.
export const getOptionalFields = (driver: string): DriverField[] =>
  getDriverFields(driver).filter((field) => !field.required)
